import React, { useEffect } from 'react';
import { Stack } from '@talentmesh/core';
import { useAppInsightsContext } from '@microsoft/applicationinsights-react-js';
import PermissionToggle from './PermissionToggle';
import { useScreenSharingContext } from '../../../Context/ScreenSharingContext';
import useTMTranslation from '../../../Hooks/useTMTranslation';
import { useCTMonitoringFormContext } from '../../../Models/CandidateTestMonitoringSettings';

function CTMonitoringTogglesPanel(): React.JSX.Element {
    const { tTest } = useTMTranslation();
    const form = useCTMonitoringFormContext();
    const { setMedia } = useScreenSharingContext();
    const { trackException } = useAppInsightsContext();

    const manageFullScreen = async (checked: boolean) => {
        try {
            if (checked && !document.fullscreenElement) {
                document.documentElement.requestFullscreen();
            } else if (!checked && document.fullscreenElement) {
                await document.exitFullscreen();
            }
            // eslint-disable-next-line no-empty
        } catch (e: any) {
            // SHIT CODE
            // need to figure out how to mock module function in Cypress (see mock-in-bundle)
            // for now have to check if code runs under it
            if (!window.hasOwnProperty('Cypress')) {
                trackException({ exception: e });
            }
        }
    };

    const manageScreenInvigilation = async (checked: boolean) => {
        let media: MediaStream | undefined;
        if (checked) {
            try {
                media = await navigator.mediaDevices.getDisplayMedia();
            } catch {
                form.setValue('isScreenInvigilationEnabled', false);
            }
        }

        setMedia(media);
    };

    const [isFullScreenEnabled, isScreenInvigilationEnabled] = form.watch([
        'isFullScreenEnabled',
        'isScreenInvigilationEnabled',
    ]);

    useEffect(() => {
        const doUseEffect = async () => {
            await manageFullScreen(isFullScreenEnabled);
        };
        doUseEffect();
    }, [isFullScreenEnabled]);

    useEffect(() => {
        const doManageInvigilation = async () => {
            await manageScreenInvigilation(isScreenInvigilationEnabled);
        };
        doManageInvigilation();
    }, [isScreenInvigilationEnabled]);

    useEffect(() => {
        const onFullscreenChange = () => {
            if (document.fullscreenElement) {
                form.setValue('isFullScreenEnabled', true);
            } else {
                form.setValue('isFullScreenEnabled', false);
            }
        };

        document.addEventListener('fullscreenchange', onFullscreenChange);
        return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
    }, []);

    return (
        <Stack direction="row" spacing={2.5} sx={{ alignItems: 'flex-start' }}>
            <PermissionToggle
                header={tTest('EnableScreenSnapshots')}
                disclaimer={tTest('EnableScreenSnapshotsDisclaimer')}
                name="isScreenInvigilationEnabled"
            />
            <PermissionToggle
                header={tTest('EnableWebcamSnapshots')}
                disclaimer={tTest('EnableWebcamSnapshotsDisclaimer')}
                name="isWebcamEnabled"
            />
            <PermissionToggle
                header={tTest('FullscreenMode')}
                disclaimer={tTest('FullscreenModeDisclaimer')}
                name="isFullScreenEnabled"
            />
        </Stack>
    );
}

export default CTMonitoringTogglesPanel;
